import { call, put, takeLatest } from 'redux-saga/effects';
import { api } from 'api/api';
import types from '../types';

// ============================================================================
// TESTING CONFIGURATION - Safe for local/dev environments only
// ============================================================================
// Set to true to use a fake site URL for testing (only works in local/dev)
const USE_FAKE_URL_FOR_TESTING = false;
// The fake site URL to use when testing (ignored in production)
const FAKE_SITE_URL = 'tipti.me';
// Playwright per-site e2e override: pass ?__test_site_url=<site_url> to test
// any site locally without hand-editing FAKE_SITE_URL and restarting the dev
// server. Same isSafeEnvironment() gate as the constant above. Checked first
// so a query param always wins if both were somehow set at once. See
// e2e/site-identity.spec.js.
// ============================================================================

/**
 * Determines if we're in a safe environment (local or dev) where fake URLs are allowed
 * @returns {boolean} True if running on localhost or dev environment
 */
const isSafeEnvironment = () => {
  const hostname = window.location.hostname;
  const href = window.location.href;

  // Allow on localhost/127.0.0.1
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return true;
  }

  // Allow on Firebase dev environment
  if (href.includes('downkount-dev.web.app')) {
    return true;
  }

  // Deny everywhere else (production, staging, etc.)
  return false;
};

function* getConfiguration({ payload }) {
  // Determine which site URL to use
  let siteUrl = payload;

  // Only allow either override in safe environments
  const testSiteUrlParam = new URLSearchParams(window.location.search).get(
    '__test_site_url'
  );
  if (testSiteUrlParam && isSafeEnvironment()) {
    console.warn(
      `🧪 E2E TEST MODE: Using site URL "${testSiteUrlParam}" from ?__test_site_url= instead of "${payload}"`
    );
    siteUrl = testSiteUrlParam;
  } else if (USE_FAKE_URL_FOR_TESTING && isSafeEnvironment()) {
    console.warn(
      `🧪 TESTING MODE: Using fake site URL "${FAKE_SITE_URL}" instead of "${payload}"`
    );
    console.warn(
      '⚠️  This only works in local/dev environments. Production will use the real URL.'
    );
    siteUrl = FAKE_SITE_URL;
  } else if (USE_FAKE_URL_FOR_TESTING && !isSafeEnvironment()) {
    console.warn(
      `🚫 TESTING MODE DISABLED: Fake URL "${FAKE_SITE_URL}" is not allowed in production. Using real URL: "${payload}"`
    );
  }

  try {
    const response = yield call(api.config.getConfig, {
      site_url: siteUrl,
    });

    const { status, data } = response;

    // Check if we got HTML instead of JSON
    if (typeof data === 'string' && data.includes('<!doctype')) {
      console.error('[Config] ERROR: Received HTML instead of JSON from API');
      console.error(
        '[Config] This usually means the backend endpoint is not found or misconfigured'
      );
      console.error('[Config] Requested URL:', siteUrl);
      console.error(
        '[Config] API endpoint:',
        `${response.config?.baseURL || ''}${response.config?.url || ''}`
      );

      yield put({
        type: types.GET_CONFIG_ERROR,
        payload:
          'Configuration API returned invalid data. Please contact support.',
      });
      return;
    }

    // console.log('[Config] Has site_name?', !!data?.site_name);
    // console.log(
    //   '[Config] Has display_leagues_select?',
    //   data?.display_leagues_select
    // );
    // console.log(
    //   '[Config] Has display_teams_select?',
    //   data?.display_teams_select
    // );

    // Fetch app data by site_id if available
    let finalData = data;
    if (data?.site_id) {
      try {
        // console.log('[Config] Fetching app data for site_id:', data.site_id);
        const appResponse = yield call(
          api.apps.getAppDataBySiteId,
          data.site_id
        );
        // console.log('[Config] App data response:', appResponse.data);

        // Merge app data into config. `data` (from siteInfoByUrl) is the
        // authoritative per-site config - spread it LAST so its fields
        // always win over appData's on any overlapping key. Confirmed via
        // live API responses that getAllAppsAndSites can return stale/null
        // values (e.g. sport_level: null) for fields siteInfoByUrl has
        // correct (e.g. sport_level: "Collegiate") - the old spread order
        // (appData last) silently let those nulls clobber the correct
        // config, breaking isCollegeLeagues in Header.jsx for any affected
        // site (confirmed: razorbackstime.downkount.com and others never
        // showed their college-league Selector because of exactly this).
        // appData should only ever ADD fields data doesn't have
        // (app_file_name, app_ios_enabled, etc.), never override them.
        if (appResponse.data && appResponse.data.length > 0) {
          const appData = appResponse.data[0]; // First app for this site
          finalData = {
            ...appData,
            ...data,
          };
          // console.log('[Config] Merged config with app data:', finalData);
        }
      } catch (appError) {
        console.warn(
          '[Config] Failed to fetch app data, continuing without it:',
          appError
        );
      }
    }

    if (status < 200 || status >= 300) {
      yield put({
        type: types.GET_CONFIG_ERROR,
        payload:
          "Something went wrong. There's an issue on our end and we can't show games. Please try again later",
      });
    } else yield put({ type: types.GET_CONFIG_SUCCESS, payload: finalData });
  } catch (error) {
    console.error('[Config] API Error:', error);
    console.error('[Config] Error response:', error.response);
    yield put({
      type: types.GET_CONFIG_ERROR,
      payload:
        "Something went wrong. There's an issue on our end and we can't show games. Please try again later",
    });
  }
}

export function* configWatcher() {
  yield takeLatest(types.GET_CONFIG_START, getConfiguration);
}
